<template>
  <breadcrumb :links="{ '/': 'Home', 'current': 'Meteogramma' }"></breadcrumb>

  <div class="container-xl p-0 py-sm-2 px-sm-3">
    <div id="main_container">
      <div v-if="given_position" id="meteogram_container" class="meteogram">
        <div v-if="loader" class="loader loading">
          <div class="lds-dual-ring"></div>
        </div>
        <canvas id="meteogram_chart" width="400" height="400"></canvas>
      </div>
      <div v-else id="meteogram_tip" class="meteogram">
        <div v-if="loader" class="loader loading">
          <div class="lds-dual-ring"></div>
        </div>
        <p>
          Selezionando una posizione sulla mappa si ottiene una <strong>rappresentazione grafica</strong> (meteogramma)
          della situazione <strong>meteo in tempo reale</strong> prendendo i valori dalle stazioni più vicine.
        </p>
        <p>
          <button class="btn btn-primary" v-on:click="useCurrentPosition">Usa la posizione attuale</button>
        </p>
      </div>
      <div id="map_container">
        <Map class="map" :markers="mapMarkers" :currentSensor="current" @mapclick="mapClicked">

          <template #popup="{ item }">
            <template v-if="item.get" class="station-tooltip">
              <p class='text-primary fw-bold text-center'>{{ item.get('name') }}</p>
              <a v-if="item.get('name') !== 'Posizione'" :href="item.get('link')" class="btn btn-primary btn-sm">Vai alla
                stazione</a>
            </template>
          </template>

        </Map>
      </div>
    </div>
  </div>

  <app-footer-mini></app-footer-mini>
</template>

<script>
import Map from '@/components/Map/BaseFvgMap'
import MonitorApi from '@/services/MonitorApi';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels'
import Labels from '@/utils/labels';
import Breadcrumb from '@/components/Breadcrumb'
import AppFooterMini from '@/app-footer-mini.vue'
import { imageMarkerUI } from '@/components/Map/MapMarkers';

export default {
  name: "meteogram",
  mixins: [Labels],
  components: { Map, Breadcrumb, AppFooterMini },
  props: {
    lat: { type: Number, default: null },
    lng: { type: Number, default: null },
  },
  data() {
    return {
      loader: false,
      measures: {},
      mapMarkers: [],
      position: (!isNaN(this.lat) && !isNaN(this.lng) ? [this.lng, this.lat] : [])
    }
  },
  computed: {
    given_position() {
      if (this.position.length != 2) return false;
      if (isNaN(this.position[0])) return false;
      if (isNaN(this.position[1])) return false;
      return true;
    }
  },
  methods: {
    mapClicked(args) {
      this.position = [args.latlon.lon, args.latlon.lat];
      this.loadChart();
    },
    useCurrentPosition() {
      this.loader = true;
      const me = this;
      navigator.geolocation.getCurrentPosition(function (pos) {
        me.position = [pos.coords.longitude, pos.coords.latitude]
        me.loadChart();
      });
    },
    smoothData(data) {
      let i = data.length;
      while (i--) {
        let value = data[i];
        // Set the smoothed value to the average of the closest points, but don't allow
        // it to differ more than 0.5 degrees from the given value
        let sum = (data[i - 1] || data[i]) + value + (data[i + 1] || data[i]);
        data[i] = Math.max(value - 0.5, Math.min(sum / 3, value + 0.5));
      }
      return data;
    },
    loadSymbols: function (symbols, r) {
      r.keys().map((item) => {
        const image = new Image();
        image.src = r(item)
        symbols[item.replace('./', '')] = image;
      });
    },
    loadChart: async function () {
      this.loader = true;
      let data = await MonitorApi.getMeteogramData(this.position);
      this.mapMarkers = []
      this.mapMarkers.push({
        lat: this.position[1],
        lon: this.position[0],
        name: "Posizione",
        ui: imageMarkerUI({ src: '/map_icons/position.png' })
      }
      )
      for (const i in data.stations) {
        const station = data.stations[i];
        this.mapMarkers.push({
          name: station.name,
          icon: 'meteo_station',
          lat: station.lat,
          lon: station.lon,
          ui: imageMarkerUI({ src: '/map_icons/meteo_station.png' }),
          link: `#/stazione/${station.id}`
        });
      }
      const labels = data.measures.map(m => this.convertOnlyTime(m.dt).substring(0, 5));
      const temperatures = data.measures.map(m => m['T']); //this.smoothData(data.measures.map(m => m['T']));
      const pressures = data.measures.map(m => m['B']);
      const rain = data.measures.map(m => Math.round(m['P'] * 10) / 10);// + parseInt(Math.random()*10)/10);
      const offset = (Math.max(...temperatures.filter(Number)) - Math.min(...temperatures.filter(Number))) / 10;
      const translated_temperatures_1 = temperatures.map(t => t + offset);
      const translated_temperatures_2 = temperatures.map(t => t - offset);
      const symbols = {}
      this.loadSymbols(symbols, require.context('@yr/weather-symbols/dist/png/30/', false, /\.(png|jpe?g|svg)$/));
      this.loadSymbols(symbols, require.context('@/assets/icons/', false, /\.(png|jpe?g|svg)$/));

      this.loader = false;
      if (this.chart)
        this.chart.destroy();
      const me = this;
      this.chart = new Chart('meteogram_chart', {
        plugins: [ChartDataLabels],
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            id: "TEMP",
            data: temperatures,
            fill: false,
            borderColor: "#FF0000",
            backgroundColor: "#FF0000",
            pointRadius: 0,
            borderWidth: 3,
            yAxisID: 'T',
            datalabels: {
              display: false
            }
          },
          {
            id: "PRES",
            data: pressures,
            fill: false,
            borderColor: "#90ed7d",
            backgroundColor: "#90ed7d",
            pointRadius: 0,
            borderWidth: 3,
            borderDash: [3, 3],
            yAxisID: 'B',
            datalabels: {
              display: false
            }
          },
          {
            id: "RAIN",
            data: rain,
            fill: false,
            borderColor: "#00AAFF",
            backgroundColor: "#00AAFF",
            pointRadius: 0,
            type: 'bar',
            yAxisID: 'P',
            datalabels: {
              align: 'end',
              anchor: 'end',
              font: {
                size: 9
              },
              display: function (context) {
                const value = context.dataset.data[context.dataIndex];
                return value != 0 && !isNaN(value);
              }
            }
          },
          {
            id: "CLOUD",
            data: translated_temperatures_1,
            fill: false,
            showLine: false,
            pointBackgroundColor: "#00000000",
            pointBorderColor: "#00000000",
            pointStyle: function (ctx) {
              const index = ctx.dataIndex;
              if (index % 2 == 1) {
                const label = labels[index];
                const day_night = (label > data.ephemerides.sunrise && label < data.ephemerides.sunset) ? 'd' : 'n';
                if (data.measures[index]['P'] < 0.1) {
                  if (data.measures[index]['CC'] < 0) {
                    return '';
                  }
                  if (data.measures[index]['CC'] < 3) {
                    return symbols['0' + (data.measures[index]['CC'] + 1) + day_night + '.png'];
                  }
                  if (data.measures[index]['CC'] == 3) {
                    return symbols['0' + data.measures[index]['CC'] + day_night + '.png'];
                  }
                  else {
                    return symbols['04.png'];
                  }
                }
                else {
                  return symbols['10.png'];
                }
              }
              else
                return '';
            },
            yAxisID: 'T',
            datalabels: {
              display: false
            }
          },
          {
            id: "WIND",
            data: translated_temperatures_2,
            fill: false,
            showLine: false,
            pointBackgroundColor: "#00000000",
            pointBorderColor: "#00000000",
            pointRotation: function (ctx) {
              const index = ctx.dataIndex;
              let direction = data.measures[index]?.['Dv']
              return direction + 180;
            },
            pointStyle: function (ctx) {
              const index = ctx.dataIndex;
              let intensity = data.measures[index]?.['Vv'];
              const wind_level = intensity > 10 ? 3 : intensity > 5 ? 2 : intensity > 0 ? 1 : 0
              if (index % 2 == 1) {
                return symbols['wind_' + wind_level + '.png'];
              }
              else
                return '';
            },
            yAxisID: 'T',
            datalabels: {
              display: false
            }
          }]
        },
        options: {
          maintainAspectRatio: false,
          animation: false,
          responsive: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          stacked: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              usePointStyle: true,
              callbacks: {
                label: function (context) {
                  const id = context.dataset.id;
                  let label = '';
                  if (id == 'TEMP') {
                    label = 'Temperatura: ';
                    if (context.parsed.y !== null)
                      label += me.formatMeasure(context.parsed.y, 1) + "°";
                  }
                  else if (id == 'RAIN') {
                    label = 'Pioggia: ';
                    if (context.parsed.y !== null)
                      label += me.formatMeasure(context.parsed.y, 1) + " mm";
                  }
                  else if (id == 'WIND') {
                    label = 'Intensità vento: ';
                    if (context.parsed.y !== null)
                      label += me.formatMeasure(context.parsed.y, 1) + " m/s";
                  }
                  else if (id == 'PRES') {
                    label = 'Pressione: ';
                    if (context.parsed.y !== null)
                      label += me.formatMeasure(context.parsed.y, 0) + " hPa";
                  }
                  else if (id == 'CLOUD') {
                    label = 'Situazione';
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            T: {
              type: 'linear',
              display: true,
              position: 'left',
              ticks: {
                callback: function (value) {
                  return value + '°';
                }
              }
            },
            B: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
              ticks: {
                color: '#90ed7d',
                callback: function (value) {
                  return value + ' hPa';
                }
              }
            },
            P: {
              type: 'linear',
              display: false,
              position: 'right',
              max: 10,
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              }
            }
          }
        }
      });
      this.chart.resize();
    }
  },
  mounted: function () {
    if (!isNaN(this.lat) && !isNaN(this.lng)) {
      this.loadChart();
    }
  }

}
</script>

<style lang="scss" scoped>
#main_container {
  position: relative;
  height: 100%;
}

@media screen and (max-width: 576px) {
  #meteogram_tip {
    padding-top: 15px !important;

    p {
      font-size: 1.2em !important;
    }
  }
}

.meteogram {
  position: absolute;
  width: 100%;
  top: 0px;
  bottom: 50%;
}

#map_container {
  position: absolute;
  width: 100%;
  top: 50%;
  bottom: 0px;
}

.map {
  height: 100%;
}

.loader {
  position: absolute;
  top: 0;
  bottom: 0;
  background-color: #ccc;
  opacity: 0.8;
  width: 100%;
}

#meteogram_tip {
  background-color: #eee;
  padding-top: 100px;

  p {
    padding: 10px 3px 0;
    font-size: 1.5em;
    color: #888;
    font-style: italic;
    text-align: center;
  }
}

.station_tooltip {
  min-width: 200px;

  h5 {
    font-size: 15px;
  }

  p {
    font-size: 14px;
  }

  a {
    color: #fff;
  }
}
</style>